import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Home, Building2 } from "lucide-react";

const CONTENT = {
  homes: {
    title: "For Homes & Beach Villas",
    intro: "Come home to a place that smells fresh, shines bright and feels like the coast should.",
    points: [
      "Weekly or one-off deep cleaning for apartments & villas",
      "Salt-stain window and balcony glass cleaning",
      "Lawn mowing, palm & tropical garden care",
      "Interior repainting and small home repairs",
    ],
  },
  business: {
    title: "For Businesses & Resorts",
    intro: "First impressions win guests. We keep your premises immaculate, on schedule, every time.",
    points: [
      "Contract cleaning for hotels, offices & restaurants",
      "Façade, glass and common-area maintenance",
      "Grounds keeping and landscape grooming",
      "Exterior weatherproof painting & repair call-outs",
    ],
  },
};

export default function Audiences() {
  const [tab, setTab] = useState("homes");
  const active = CONTENT[tab];

  return (
    <section id="audiences" className="bg-[#F3EDE2] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end"
        >
          <div className="max-w-xl">
            <p className="font-jbm text-xs font-semibold uppercase tracking-[0.3em] text-[#C85A32]">Who we serve</p>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1C3B2B] sm:text-4xl lg:text-5xl">
              Private homes. Busy businesses. Same shine.
            </h2>
          </div>

          <div className="flex rounded-full border border-[#E4DDD3] bg-white p-1.5 shadow-sm">
            <button
              data-testid="homes-tab-button"
              onClick={() => setTab("homes")}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color] duration-300 ${
                tab === "homes" ? "bg-[#1C3B2B] text-[#FBF9F5]" : "text-[#525B53] hover:text-[#1C3B2B]"
              }`}
            >
              <Home size={15} /> Homes
            </button>
            <button
              data-testid="businesses-tab-button"
              onClick={() => setTab("business")}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color] duration-300 ${
                tab === "business" ? "bg-[#1C3B2B] text-[#FBF9F5]" : "text-[#525B53] hover:text-[#1C3B2B]"
              }`}
            >
              <Building2 size={15} /> Businesses
            </button>
          </div>
        </motion.div>

        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl border border-[#E4DDD3] bg-white p-8 shadow-[0_30px_60px_-35px_rgba(28,59,43,0.35)] sm:p-12"
            >
              <h3 className="font-display text-2xl font-bold text-[#1C3B2B] sm:text-3xl">{active.title}</h3>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#525B53] sm:text-lg">{active.intro}</p>
              <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {active.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-2xl bg-[#FBF9F5] p-4 text-sm font-medium text-[#1A1C18]">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1C3B2B] text-[#FBF9F5]">
                      <Check size={13} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
